import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { type UserData, getUserData } from "@/utils/auth-api";

interface AuthState {
  token: string | null;
  userId: string | null;
  user: UserData | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (token: string, userId: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  token: null,
  userId: null,
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

const STORAGE_KEY_TOKEN = "auth_token";
const STORAGE_KEY_USER_ID = "auth_user_id";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEY_TOKEN)
  );
  const [userId, setUserId] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEY_USER_ID)
  );
  const [user, setUser] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(!!token);

  const login = useCallback((newToken: string, newUserId: string) => {
    localStorage.setItem(STORAGE_KEY_TOKEN, newToken);
    localStorage.setItem(STORAGE_KEY_USER_ID, newUserId);
    setToken(newToken);
    setUserId(newUserId);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY_TOKEN);
    localStorage.removeItem(STORAGE_KEY_USER_ID);
    setToken(null);
    setUserId(null);
    setUser(null);
  }, []);

  // Fetch user data on mount / token change
  useEffect(() => {
    if (!token || !userId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getUserData(userId, token)
      .then((userData) => {
        if (!cancelled && userData) {
          setUser(userData);
        }
      })
      .catch(() => {
        // Don't logout on fetch failure — token may still be valid
        // User data is nice-to-have for display
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, userId, logout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      userId,
      user,
      isAuthenticated: !!token,
      isLoading,
      login,
      logout,
    }),
    [token, userId, user, isLoading, login, logout]
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
